/** Sieve — Chaînes françaises (§11). */

import type { Dict } from './types';

export const fr: Dict = {
  meta: { name: 'Français', dir: 'ltr' },
  brand: 'Sieve',
  tagline: 'Trouvez les 5 nombres premiers.',

  header: { menu: 'Menu', language: 'Langue', stats: 'Statistiques', help: 'Comment jouer' },

  play: {
    puzzleLabel: 'Sieve nº {id}',
    primesLeft: 'Premiers',
    mistakes: 'Erreurs',
    loading: 'Chargement de la grille du jour…',
    offline: 'Hors ligne — lecture depuis la file en cache.',
  },

  outcome: {
    flawless: 'Sans faute',
    solved: 'Résolu',
    failed: 'Plus d’essais',
  },

  messages: {
    flawless: [
      'Pas un seul geste de trop.',
      'Nettoyage impeccable.',
      'Vous avez vu clair du premier coup.',
      'Rien ne vous a échappé.',
      'Un crible de manuel.',
    ],
    solved: [
      'Tamisé, au bout du compte.',
      'Quelques frayeurs. Résolu quand même.',
      'Les survivants ont résisté. Vous avez gagné.',
      'Vous y êtes arrivé.',
      'Composés évités, premiers trouvés.',
    ],
    failed: [
      'La grille a gardé quelques secrets.',
      'Trois pièges déclenchés. À demain pour un autre crible.',
      'Presque ne suffit pas à vider la grille.',
      'Certains composés portent un bon déguisement.',
      'À demain — les multiples de 7 vous attendent.',
    ],
  },

  cell: { composite: 'composé', prime: 'premier' },

  mult: {
    banner: 'Divisible par {x}',
    findAll: 'trouvez-en 5',
    found: 'Trouvés',
    divisible: 'divisible par {x}',
    notDivisible: 'non divisible par {x}',
    remainder: 'reste {r}',
    menuToday: 'Aujourd’hui — Multiples',
    primeToday: 'Aujourd’hui — Sieve · premiers',
    harder: 'plus dur',
    onboard1Title: 'Trouvez les multiples',
    onboard1Body: 'On vous donne un nombre — 7 par exemple. Touchez les 5 nombres qui en sont divisibles. Trois erreurs permises.',
    onboard2Title: 'Raccourcis',
    onboard2Body: '÷2 se termine par un chiffre pair · ÷5 se termine par 0 ou 5 · ÷3 la somme des chiffres est un multiple de 3. Pour le reste, divisez.',
    messages: {
      flawless: [
        'Chaque multiple, aucun faux pas.',
        'Vous aviez le bon chiffre.',
        'Division impeccable.',
        'Aucun reste ne vous a trompé.',
        'Droit à travers le crible.',
      ],
      solved: [
        'Tous trouvés, au bout du compte.',
        'Une ou deux frayeurs. Résolu quand même.',
        'Les pièges ont failli vous avoir.',
        'Diviser pour régner.',
        'Jusqu’au dernier.',
      ],
      failed: [
        'Un peu trop de restes aujourd’hui.',
        'Les faux amis ont fait leur travail.',
        'À deux doigts du sans-faute.',
        'Certains avaient vraiment l’air divisibles.',
        'À demain — un nouveau diviseur vous attend.',
      ],
    },
  },

  end: {
    nextIn: 'Prochaine grille dans',
    share: 'Partager',
    copied: 'Copié dans le presse-papiers',
    shared: 'Partagé',
    shareFailed: 'Partage impossible',
    archive: 'Jouer les grilles passées',
    keepGoing: 'Continuer → Rapide',
    yourResult: 'Votre résultat',
  },

  stats: {
    title: 'Statistiques',
    played: 'Parties',
    winPct: '% de réussite',
    currentStreak: 'Série en cours',
    maxStreak: 'Meilleure série',
    flawless: 'Sans faute',
    flawlessPct: '% sans faute',
    distribution: 'Répartition des erreurs',
    rapidBest: 'Record rapide',
    buckets: { '0': '0', '1': '1', '2': '2', failed: 'Échec' },
    none: 'Aucune partie. Jouez la grille du jour.',
  },

  help: {
    title: 'Comment jouer',
    card1Title: 'Trouvez les 5 premiers',
    card1Body: 'Vingt-cinq nombres à trois chiffres. Exactement cinq sont premiers. Touchez-les. Trois erreurs permises.',
    card2Title: 'Éliminations rapides',
    card2Body: 'Pair → dehors. Se termine par 5 → dehors. Somme des chiffres multiple de 3 → dehors.',
    card3Title: 'Puis les pièges',
    card3Body: 'Le reste se vérifie avec 7, 11, 13, et jusqu’à 31. C’est tout le jeu.',
    cheatTitle: 'Aide-mémoire de divisibilité',
    cheat: [
      '2 — dernier chiffre pair',
      '3 — somme des chiffres multiple de 3',
      '5 — se termine par 0 ou 5',
      '7 — pas d’astuce ; divisez',
      '11 — somme alternée égale à 0 ou ±11',
      '13 — pas d’astuce ; divisez',
      '17–31 — les tenaces ; vérifiez chacun',
    ],
    skip: 'Passer',
    start: 'Commencer',
    next: 'Suivant',
  },

  settings: {
    title: 'Réglages',
    highContrast: 'Contraste élevé',
    colorBlind: 'Palette adaptée au daltonisme',
    language: 'Langue',
    auto: 'Automatique',
  },

  rapid: {
    title: 'Rapide',
    subtitle: 'Un nombre à la fois. Jusqu’où irez-vous ?',
    prime: 'Premier',
    notPrime: 'Non premier',
    streak: 'Série',
    best: 'Record',
    gameOver: 'Partie terminée',
    was: 'était',
    playAgain: 'Rejouer',
    back: 'Retour au quotidien',
    start: 'Commencer',
  },

  menu: {
    title: 'Menu',
    daily: 'Grille du jour',
    rapid: 'Mode rapide',
    archive: 'Archives',
    stats: 'Statistiques',
    settings: 'Réglages',
    help: 'Comment jouer',
    privacy: 'Confidentialité',
    terms: 'Conditions',
    attributions: 'Attributions',
  },

  archive: {
    title: 'Archives',
    subtitle: 'Grilles passées. Sans effet sur votre série.',
    play: 'Jouer',
    done: 'Terminé',
    locked: 'Pas encore disponible',
    back: 'Retour',
  },

  common: { close: 'Fermer', on: 'Activé', off: 'Désactivé' },
};
